import { Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, createRef, MouseEvent, ReactNode, useMemo, useState } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import SearchInput from 'dashboard/components-lib/SearchInput';
import TableLayout, { TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import Tooltip from 'design-system/components/Tooltip';
import { BASE } from 'design-system/variables/spacing';

const getDifference = (setA: Set<string>, setB: Set<string>) =>
  Array.from(setA).filter((x) => !setB.has(x));

export interface SearchListOption {
  name: string;
  value: string;
  categories: string[];
}

interface SearchListsSelectTableProps {
  options: SearchListOption[];
  values: string[];
  headerWidth?: string;
  valueDisplayName: string;
  categoryDisplayName: string;
  categoryDisplayMap?: Record<string, string>;
  selectAllDisplay?: ReactNode;
  disabledValues?: Set<string>;
  hideFilters?: boolean;
  viewMode?: boolean;
  optionDisplay?: (option: SearchListOption) => ReactNode;
  onChange?: (values: string[]) => unknown;
}

export const SearchListsSelectTable = ({
  options,
  values,
  headerWidth,
  selectAllDisplay,
  valueDisplayName = 'List name',
  categoryDisplayName = 'List category',
  categoryDisplayMap,
  disabledValues = new Set(),
  hideFilters = false,
  viewMode = false,
  optionDisplay,
  onChange,
}: SearchListsSelectTableProps) => {
  const [selected, setSelected] = useState<Set<string>>(
    new Set([...values, ...Array.from(disabledValues)]),
  );
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSortDescending, setIsSortDescending] = useState(false);

  const categoriesMap = useMemo(
    () =>
      options.reduce(
        (map, option) => {
          for (const cat of option.categories) {
            map[cat] = map[cat] ? map[cat] + 1 : 1;
          }
          return map;
        },
        {} as Record<string, number>,
      ),
    [options],
  );

  const filteredOptions = useMemo(() => {
    let filtered = viewMode
      ? options.filter((option) => values.includes(option.value))
      : [...options];

    if (searchTerm.length > 0) {
      filtered = filtered.filter((option) =>
        option.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    } else {
      if (selectedCategory !== 'all') {
        filtered = filtered.filter((option) => option.categories.includes(selectedCategory));
      }
    }
    filtered.sort((a, b) => a.name.localeCompare(b.name));

    if (isSortDescending) {
      filtered.reverse();
    }

    return filtered;
  }, [options, searchTerm, selectedCategory, isSortDescending, viewMode, values]);

  const allChecked = filteredOptions.every((option) => selected.has(option.value));
  const partiallyChecked = filteredOptions.some((option) => selected.has(option.value));
  const allDisabled = filteredOptions.every((option) => disabledValues.has(option.value));
  const tableRef = createRef<HTMLDivElement>();

  const handleOptionClick = (value: string) => (e: ChangeEvent<HTMLInputElement>) => {
    const newSelected = new Set(selected);
    if (e.target.checked) {
      newSelected.add(value);
    } else {
      newSelected.delete(value);
    }
    setSelected(newSelected);
    if (onChange) {
      onChange(getDifference(newSelected, disabledValues));
    }
  };

  const handleCheckAllOptionChange = (e: ChangeEvent<HTMLInputElement>) => {
    const filteredValues = filteredOptions.map((option) => option.value);
    const newSelected = e.target.checked
      ? new Set([...Array.from(selected), ...filteredValues])
      : new Set(Array.from(selected).filter((val) => !filteredValues.includes(val)));
    setSelected(newSelected);
    if (onChange) {
      onChange(getDifference(newSelected, disabledValues));
    }
  };

  const handleCategoryClick = (category: string) => (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setSelectedCategory(category);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (tableRef.current.firstChild) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (tableRef.current.firstChild as HTMLDivElement).scrollTop = 0;
    }
  };

  const updateSearchTerm = (term: string) => {
    setSelectedCategory('all');
    setSearchTerm(term);
  };

  return (
    <>
      <SearchInput
        title={null}
        value=''
        handleInput={updateSearchTerm}
        placeholder={valueDisplayName}
      />
      <Spacer height={viewMode ? 0.5 : 2} />
      {searchTerm === '' && !hideFilters && (
        <>
          <FilterContainer>
            <StickerButton
              key='all'
              id='search-lists-select-option__button-all'
              variant={selectedCategory === 'all' ? 'primary' : 'basic'}
              width='auto'
              onClick={handleCategoryClick('all')}
              size='sm'
            >
              {`All (${options.length})`}
            </StickerButton>
            {Object.keys(categoriesMap).map((category) =>
              categoryDisplayMap ? (
                <Tooltip
                  key={category}
                  side='top'
                  trigger={
                    <StickerButton
                      id={`search-lists-select-option__button-${category}`}
                      variant={selectedCategory === category ? 'primary' : 'basic'}
                      width='auto'
                      onClick={handleCategoryClick(category)}
                      size='sm'
                    >
                      {category}
                    </StickerButton>
                  }
                >
                  <TooltipWrapper>{categoryDisplayMap[category]}</TooltipWrapper>
                </Tooltip>
              ) : (
                <StickerButton
                  key={category}
                  id={`search-lists-select-option__button-${category}`}
                  variant={selectedCategory === category ? 'primary' : 'basic'}
                  width='auto'
                  onClick={handleCategoryClick(category)}
                  size='sm'
                >
                  {category}
                </StickerButton>
              ),
            )}
          </FilterContainer>
          <Spacer height={viewMode ? 0.5 : 2} />
        </>
      )}
      <TableLayoutWrapper ref={tableRef}>
        <TableLayout
          nullState={<div>No options</div>}
          headerTR={
            <>
              {!viewMode && <TableTH isSticky={true}></TableTH>}
              <ValueTableTH
                id='search-lists-select__value-header'
                canSort={true}
                isSorted={true}
                isDescending={isSortDescending}
                width={headerWidth}
                isSticky={true}
                onClick={() => {
                  setIsSortDescending(!isSortDescending);
                }}
              >
                {valueDisplayName}
              </ValueTableTH>
              <TableTH isSticky={true}>{categoryDisplayName}</TableTH>
            </>
          }
        >
          {filteredOptions.length > 0 && !viewMode && (
            <TableTR>
              <CheckboxTableTD>
                <Checkbox
                  id='search-lists-select-option__all'
                  checked={allChecked}
                  indeterminate={partiallyChecked && !allChecked}
                  disabled={allDisabled}
                  onChange={handleCheckAllOptionChange}
                />
              </CheckboxTableTD>
              <MainTD>
                <StyledLabel htmlFor='search-lists-select-option__all'>
                  {selectAllDisplay ? selectAllDisplay : `Select all (${filteredOptions.length})`}
                </StyledLabel>
              </MainTD>
              <CategoryTD></CategoryTD>
            </TableTR>
          )}
          {filteredOptions.length === 0 && (
            <TableTR>
              {!viewMode && <CheckboxTableTD></CheckboxTableTD>}
              <TableTD>
                {searchTerm ? `No matches found for "${searchTerm}"` : 'No options selected'}
              </TableTD>
              <CategoryTD></CategoryTD>
            </TableTR>
          )}
          {filteredOptions.map((option) => (
            <TableTR key={option.name}>
              {!viewMode && (
                <CheckboxTableTD>
                  <Checkbox
                    id={`search-lists-select-option__${option.value}`}
                    checked={selected.has(option.value) || disabledValues.has(option.value)}
                    disabled={disabledValues.has(option.value)}
                    onChange={handleOptionClick(option.value)}
                  />
                </CheckboxTableTD>
              )}

              <MainTD>
                <StyledLabel htmlFor={`search-lists-select-option__${option.value}`}>
                  {optionDisplay ? optionDisplay(option) : <span>{option.name}</span>}
                </StyledLabel>
              </MainTD>
              <CategoryTD>
                {option.categories.map((category) => (
                  <StyledSticker key={category}>{category}</StyledSticker>
                ))}
              </CategoryTD>
            </TableTR>
          ))}
        </TableLayout>
      </TableLayoutWrapper>
    </>
  );
};

const LABEL_MIN_HEIGHT = 62;
const CHECKBOX_HEIGHT = 16;

const StyledLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  min-height: ${LABEL_MIN_HEIGHT}px;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const StickerButton = styled(Button)`
  border-radius: ${BORDER_RADIUSES.pill};
  margin-right: ${Spacing[2]};
  padding: ${Spacing[2]} ${Spacing[3]};
  height: auto;
`;

const ValueTableTH = styled(TableTH)`
  width: ${({ width = '75%' }) => width};
`;

const CheckboxTableTD = styled(TableTD)`
  width: ${BASE * 2.5}px;
  padding: ${LABEL_MIN_HEIGHT / 2 - CHECKBOX_HEIGHT / 2}px 0 0 ${Spacing[4]};
  vertical-align: top;
`;

const StyledSticker = styled(Sticker)`
  &:not(:first-child) {
    margin-left: ${Spacing[2]};
  }
`;

const MainTD = styled(TableTD)`
  padding: 0px ${Spacing[4]};
`;

const CategoryTD = styled(TableTD)`
  padding: ${Spacing[4]} ${Spacing[4]} 0 ${Spacing[4]};
  vertical-align: top;
`;

const TableLayoutWrapper = styled.div`
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
  border-width: 1px;
  border-style: solid;
  border-color: ${({ theme }) => theme.color.border.input};
  border-radius: ${BORDER_RADIUSES.rounded};

  & > div {
    height: 400px;
  }
`;

const TooltipWrapper = styled.span`
  max-width: 400px;
`;
